/**
 * NigeriaBankTransferModal — manual Moniepoint transfer flow for
 * Nigerian subscribers.
 *
 * Feb 2026 user spec: extra payment option (transfer) for Nigeria
 * subscriptions only. Receipt goes via WhatsApp to +2348064002451,
 * beneficiary is Hallow Reflections Limited (Moniepoint · 8064002451).
 *
 * Flow inside this component
 *   Step 1 — user hits "Bank Transfer" on /premium.
 *   Step 2 — modal POSTs /api/payments/subscription/bank-transfer to
 *            reserve a reference (SUB-XXXXXXXX) + returns bank details.
 *   Step 3 — user copies details, transfers via their banking app,
 *            WhatsApps the receipt to the number shown.
 *   Step 4 — user clicks "I've sent the receipt" → POST
 *            /receipt-sent → status flips to `awaiting_admin`.
 *   Step 5 — admin approves in the Admin panel → subscription is
 *            activated server-side (see subscription_bank_transfer.py).
 */
import React, { useState } from 'react';
import { X, Copy, CheckCircle2, MessageSquare, Landmark, User, Hash, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';
import { PAYMENTS_DISABLED, PAYMENTS_UNAVAILABLE_MESSAGE } from '../utils/paymentAvailability';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CopyBtn = ({ value, testId }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(String(value));
          setCopied(true);
          toast.success('Copied');
          setTimeout(() => setCopied(false), 1200);
        } catch {
          toast.error('Copy failed');
        }
      }}
      className="ml-2 p-1 rounded hover:bg-white/10 text-slate-300"
      data-testid={testId}
      aria-label="Copy"
    >
      {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

const NigeriaBankTransferModal = ({
  open,
  onClose,
  planTier,
  planMonths,
  amountNgn,
  currencySymbol = '₦',
}) => {
  const [step, setStep] = useState('confirm');   // confirm | show_details | receipt_sent
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState(null);  // { transfer_id, reference, bank:{...}, whatsapp_msisdn, ... }

  React.useEffect(() => {
    if (!open) {
      // Reset when the modal is closed so the next open starts clean.
      setStep('confirm');
      setDetails(null);
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  if (PAYMENTS_DISABLED) {
    return (
      <div
        className="fixed inset-0 z-[210] flex items-center justify-center px-4 py-6 bg-black/70 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        data-testid="ng-bank-transfer-payments-disabled"
      >
        <div className="w-full max-w-lg rounded-2xl border border-amber-300/35 bg-slate-900 p-6 text-center shadow-2xl">
          <Landmark className="mx-auto h-10 w-10 text-amber-300" />
          <h2 className="mt-3 text-lg font-bold text-white">Bank-transfer checkout unavailable</h2>
          <p className="mt-2 text-sm text-amber-100">{PAYMENTS_UNAVAILABLE_MESSAGE}</p>
          <Button onClick={onClose} className="mt-5 bg-slate-700 hover:bg-slate-600" data-testid="ng-bank-transfer-disabled-close">
            Close
          </Button>
        </div>
      </div>
    );
  }

  const startTransfer = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/payments/subscription/bank-transfer`, {
        method: 'POST',
        body: JSON.stringify({ plan_tier: planTier, plan_months: planMonths }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Could not start transfer');
      setDetails(data);
      setStep('show_details');
    } catch (e) {
      toast.error(e.message || 'Failed to start transfer');
    } finally {
      setBusy(false);
    }
  };

  const markReceiptSent = async () => {
    if (!details?.transfer_id) return;
    setBusy(true);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/payments/subscription/bank-transfer/${details.transfer_id}/receipt-sent`,
        { method: 'POST' },
      );
      const data = await r.json();
      if (!r.ok || !data.updated) throw new Error('Could not update status');
      setStep('receipt_sent');
      toast.success('Thanks — an admin will confirm shortly.');
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const openWhatsApp = () => {
    if (!details?.whatsapp_msisdn) return;
    const msisdn = String(details.whatsapp_msisdn).replace(/[^\d]/g, '');
    const text = encodeURIComponent(
      `Hi, I've transferred ${currencySymbol}${Number(details.amount).toLocaleString()} for my Smarter Learning Hub subscription.\n\nReference: ${details.reference}\n\nAttaching my receipt.`,
    );
    window.open(`https://wa.me/${msisdn}?text=${text}`, '_blank', 'noopener');
  };

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center px-4 py-6 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="ng-bank-transfer-modal"
    >
      <div className="w-full max-w-lg bg-slate-900 border-2 border-emerald-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-800 bg-gradient-to-r from-emerald-900/40 to-slate-900">
          <div className="flex items-start gap-3 min-w-0">
            <Landmark className="w-6 h-6 text-emerald-300 mt-0.5" />
            <div>
              <h2 className="text-white text-base font-black">
                Pay by Bank Transfer 🇳🇬
              </h2>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Moniepoint · Hallow Reflections Limited · {planMonths}-month plan
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 -mr-1"
            data-testid="ng-bank-transfer-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {step === 'confirm' && (
            <>
              <p className="text-sm text-slate-200">
                You're about to pay <strong className="text-emerald-300">{currencySymbol}{Number(amountNgn).toLocaleString()}</strong> for your <strong className="text-white">{planMonths === 12 ? '1-year' : planMonths + '-month'}</strong> Premium subscription via bank transfer.
              </p>
              <ul className="text-[13px] text-slate-300 space-y-1.5 list-disc pl-5">
                <li>We'll reserve a unique reference for your transfer.</li>
                <li>You'll transfer the money from your banking app to our Moniepoint account.</li>
                <li>Send the receipt to our WhatsApp — subscription activates once an admin confirms.</li>
              </ul>
              <p className="text-[11px] text-amber-300/80">
                ⏱ Confirmations typically happen within a few hours during working days.
              </p>
            </>
          )}

          {step === 'show_details' && details && (
            <>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 text-xs uppercase tracking-widest">Amount</span>
                  <span className="text-emerald-200 font-black text-lg tabular-nums">
                    {currencySymbol}{Number(details.amount).toLocaleString()}
                  </span>
                </div>
              </div>
              <DetailRow icon={<Landmark className="w-4 h-4" />} label="Bank" value={details.bank?.name} testId="ng-bank-name" />
              <DetailRow icon={<Hash className="w-4 h-4" />} label="Account Number" value={details.bank?.account} testId="ng-bank-account" mono />
              <DetailRow icon={<User className="w-4 h-4" />} label="Account Name" value={details.bank?.holder} testId="ng-bank-holder" />
              <DetailRow icon={<Hash className="w-4 h-4 text-amber-300" />} label="Reference (put in narration)" value={details.reference} testId="ng-bank-reference" mono highlight />
              <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-3 space-y-2">
                <p className="text-[12px] text-slate-300">
                  After your transfer, send the receipt to our WhatsApp with the reference above:
                </p>
                <Button
                  onClick={openWhatsApp}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold"
                  data-testid="ng-bank-open-whatsapp"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Open WhatsApp — {details.whatsapp_msisdn}
                </Button>
              </div>
            </>
          )}

          {step === 'receipt_sent' && (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
              <h3 className="text-white text-lg font-bold">Receipt marked as sent</h3>
              <p className="text-sm text-slate-300">
                An admin will confirm your transfer and activate your subscription. You'll get a notification the moment it's live.
              </p>
              <p className="text-[11px] text-slate-500">
                Reference: <code className="text-slate-300">{details?.reference}</code>
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 bg-slate-950/60 flex items-center justify-end gap-2">
          {step === 'confirm' && (
            <>
              <Button variant="ghost" onClick={onClose} className="text-slate-300">Cancel</Button>
              <Button
                onClick={startTransfer}
                disabled={busy}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold"
                data-testid="ng-bank-start"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Get transfer details'}
              </Button>
            </>
          )}
          {step === 'show_details' && (
            <>
              <Button variant="ghost" onClick={onClose} className="text-slate-300">Not now</Button>
              <Button
                onClick={markReceiptSent}
                disabled={busy}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                data-testid="ng-bank-receipt-sent"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "I've sent the receipt"}
              </Button>
            </>
          )}
          {step === 'receipt_sent' && (
            <Button onClick={onClose} className="bg-slate-700 hover:bg-slate-600" data-testid="ng-bank-done">
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ icon, label, value, testId, mono, highlight }) => (
  <div
    className={`rounded-lg border ${highlight ? 'border-amber-400/50 bg-amber-500/10' : 'border-slate-700 bg-slate-800/60'} p-3 flex items-center gap-3`}
    data-testid={`${testId}-row`}
  >
    <div className={`p-2 rounded-md ${highlight ? 'bg-amber-500/20 text-amber-200' : 'bg-slate-700/50 text-slate-300'}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
      <p className={`text-white ${mono ? 'font-mono' : ''} text-sm font-semibold truncate`} data-testid={testId}>
        {value}
      </p>
    </div>
    <CopyBtn value={value} testId={`${testId}-copy`} />
  </div>
);

export default NigeriaBankTransferModal;
